import React, { useMemo, useState, useRef } from 'react';
import OlcButton from '~/components/General/OlcButton';
import { useParams, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';

// MUI
import {
  Avatar,
  Box,
  Chip,
  Divider,
  GlobalStyles,
  Grid,
  IconButton,
  Paper,
  Tab,
  Tabs,
  Typography,
  Checkbox,
  FormControlLabel,
  CircularProgress,
  Skeleton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

// Assets
import {
  CompanyArchiveBtnIcon,
  GeneralArrowBackCIcon,
  GeneralLocationCIcon,
} from '~/assets/images';

import General from './General';
import BrandingAssets from './BrandingAssets';
import SenderProfiles from './SenderProfiles';
import ArchiveModal from '../ArchiveModal';
import { updateOrganization } from '~/redux/actions/general-actions';
import { success, failure } from '~/redux/actions/snackbar-actions';

// Styles
import './styles.scss';
import TruncatedText from '~/components/General/TruncatedText';

const stringToColor = (str) => {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = str.charCodeAt(i) + ((hash << 5) - hash);
  }

  // Generate RGB values with a bias towards lighter colors
  const r = Math.max(180, (hash & 0xff0000) >> 16); // Ensure minimum brightness
  const g = Math.max(180, (hash & 0x00ff00) >> 8); // Ensure minimum brightness
  const b = Math.max(180, hash & 0x0000ff); // Ensure minimum brightness

  // Convert to hex and ensure it's uppercase
  const toHex = (num) => {
    const hex = Math.min(255, num).toString(16).toUpperCase();
    return hex.length === 1 ? '0' + hex : hex;
  };

  return '#' + toHex(r) + toHex(g) + toHex(b);
};

const getInitials = (name) => {
  if (!name || typeof name !== 'string') return ''; // fallback for empty/null/undefined

  const parts = name.trim().split(' ').filter(Boolean);

  if (parts.length === 0) return ''; // empty after trim
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();

  return (parts[0][0] + parts[1][0]).toUpperCase();
};

const CompanyProfile = (props) => {
  const { company: companyProp } = props;
  const params = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // Get user data from Redux state
  const user = useSelector((state) => state.userReducers?.user || {});
  const userLoading = useSelector((state) => state.userReducers?.loading || false);

  // Check if user data is still loading (empty or no organization data)
  const isUserDataLoading = userLoading || !user?.organization?.name;

  // Create company data from user state
  const company = useMemo(() => {
    if (companyProp) {
      return companyProp;
    }

    // Build address from user data
    const addressParts = [user.address1].filter(Boolean);

    const fullAddress = addressParts.length > 0 ? addressParts.join(', ') : 'No address provided';

    return {
      id: user.id || 1,
      name: user?.organization?.name || '',
      address: user?.organization?.address || '',
      logo: user.logo || null,
    };
  }, [companyProp, user]);

  const [tab, setTab] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  const [isArchiveModalOpen, setIsArchiveModalOpen] = useState(false);
  const [saveLoading, setSaveLoading] = useState(false);
  const [formState, setFormState] = useState({ isValid: false, dirty: false });
  const [formikInstance, setFormikInstance] = useState(null);

  const CHARACTER_LIMIT = 100;

  // Validation schema
  const validationSchema = Yup.object({
    name: Yup.string()
      .required('Company name is required')
      .max(CHARACTER_LIMIT, `Company name must not exceed ${CHARACTER_LIMIT} characters`)
      .trim(),
    address: Yup.string()
      .max(CHARACTER_LIMIT, `Address must not exceed ${CHARACTER_LIMIT} characters`)
      .trim(),
  });

  const handleArchiveClick = () => {
    setIsArchiveModalOpen(true);
  };

  const handleArchiveConfirm = async () => {
    // Add archive logic here
    console.log('Archiving company:', company.name);
    // You can add API call here to archive the company
  };

  const handleSaveCompany = async (values, { setFieldError, setSubmitting }) => {
    try {
      setSaveLoading(true);
      setSubmitting(true);

      const name = (values.name || '').trim();
      const address = (values.address || '').trim();

      await dispatch(
        updateOrganization({
          name: name || company.name || '',
          address: address || company.address || '',
        })
      );

      // Show success message
      dispatch(success('Company profile updated successfully'));

      setIsEditing(false);
    } catch (e) {
      // Show error message
      const errorMessage =
        e?.response?.data?.message || e?.message || 'Failed to update company profile';
      dispatch(failure(errorMessage));

      // Set field errors if they exist
      if (e?.response?.data?.errors) {
        Object.keys(e.response.data.errors).forEach((field) => {
          setFieldError(field, e.response.data.errors[field]);
        });
      }
    } finally {
      setSaveLoading(false);
      setSubmitting(false);
    }
  };

  const handleCloseArchiveModal = () => {
    setIsArchiveModalOpen(false);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
  };

  const handleSaveClick = () => {
    // Try to submit the form by clicking the hidden submit button
    const form = document.getElementById('company-profile-form');
    if (form) {
      const submitButton = form.querySelector('button[type="submit"]');
      if (submitButton) {
        submitButton.click();
      }
    }
  };

  return (
    <>
      <GlobalStyles styles={{ body: { backgroundColor: '#f9fafb' } }} />
      <Box className="olcWrapper companyProfileWrapper" sx={{ marginBottom: 0 }}>
        <Box className="olcScreenCard" style={{ paddingBottom: '20px' }}>
          <Paper
            elevation={0}
            className={`cpHeader ${isEditing ? 'cpHeaderForm' : 'cpHeaderWithoutEdits'}`}
          >
            <div className="cpHeaderLeft">
              <Box style={{ height: '72px', width: '76px' }}>
                <Avatar
                  src={company.logo}
                  sx={{
                    width: 76,
                    height: 76,
                    bgcolor: stringToColor(company.name),
                    fontFamily: 'Inter',
                    fontWeight: 600,
                    fontStyle: 'normal', // no direct "Semi Bold", it's covered by weight 600
                    fontSize: '24px',
                    lineHeight: '100%',
                    letterSpacing: '0',
                    color: 'rgba(107, 114, 128, 1)',
                  }}
                  className={`cpAvatar ${isEditing ? '' : ''}`}
                >
                  {!company.logo && getInitials(company.name)}
                </Avatar>
              </Box>
              <Box className="cpHeading">
                {isEditing ? (
                  <Formik
                    initialValues={{
                      name: company.name || '',
                      address: company.address || '',
                    }}
                    validationSchema={validationSchema}
                    onSubmit={handleSaveCompany}
                    enableReinitialize
                  >
                    {(formikProps) => {
                      const {
                        values,
                        errors: formikErrors,
                        touched,
                        handleChange,
                        handleBlur,
                        setFieldValue,
                        isSubmitting,
                        isValid,
                        dirty,
                      } = formikProps;

                      // Store formik instance for save button
                      React.useEffect(() => {
                        setFormikInstance(formikProps);
                        setFormState({ isValid, dirty });
                      }, [formikProps, isValid, dirty]);

                      return (
                        <Form id="company-profile-form">
                          <Box
                            sx={{ width: '100%', marginBottom: '8px' }}
                            className="cpInlineInputWrapper"
                          >
                            <Field
                              as="input"
                              name="name"
                              className={`cpInlineInput cpTitleInput ${
                                formikErrors.name && touched.name ? 'cpInputError' : ''
                              }`}
                              value={values.name}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              placeholder="Enter company name"
                            />
                            {values.name && (
                              <IconButton
                                size="small"
                                className="cpClearInputBtn"
                                onClick={() => setFieldValue('name', '')}
                                edge="end"
                                aria-label="clear company name"
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            )}
                            {formikErrors.name && touched.name && (
                              <Typography className="cpErrorText">{formikErrors.name}</Typography>
                            )}
                          </Box>
                          <Box sx={{ width: '100%' }} className="cpInlineInputWrapper">
                            <Field
                              as="input"
                              name="address"
                              className={`cpInlineInput cpSubtitleInput ${
                                formikErrors.address && touched.address ? 'cpInputError' : ''
                              }`}
                              value={values.address}
                              onChange={handleChange}
                              onBlur={handleBlur}
                              placeholder="Enter address"
                            />
                            {values.address && (
                              <IconButton
                                size="small"
                                className="cpClearInputBtn"
                                onClick={() => setFieldValue('address', '')}
                                edge="end"
                                aria-label="clear company address"
                              >
                                <CloseIcon fontSize="small" />
                              </IconButton>
                            )}
                            {formikErrors.address && touched.address && (
                              <Typography className="cpErrorText">
                                {formikErrors.address}
                              </Typography>
                            )}
                          </Box>
                          <button type="submit" style={{ display: 'none' }} />
                        </Form>
                      );
                    }}
                  </Formik>
                ) : (
                  <Box style={{ height: '56px' }}>
                    {isUserDataLoading ? (
                      <Skeleton
                        variant="text"
                        width="150px"
                        height={24}
                        sx={{
                          fontSize: '1.5rem',
                          fontWeight: 600,
                          lineHeight: '1.2',
                        }}
                      />
                    ) : (
                      <TruncatedText
                        className="cpTitle"
                        maxLength={30}
                        text={company?.name || 'N/A'}
                      />
                    )}

                    <div
                      style={{
                        marginTop: '12px',
                        display: 'flex',
                        gap: '8px',
                        alignItems: 'center',
                        width: '100%',
                      }}
                    >
                      <img
                        style={{
                          height: '16.66px',
                          width: '15px',
                          flexShrink: 0,
                        }}
                        src={GeneralLocationCIcon}
                        alt="location"
                      />
                      <div style={{ flex: 1, minWidth: 0, overflow: 'hidden' }}>
                        {isUserDataLoading ? (
                          <Skeleton
                            variant="text"
                            width="80%"
                            height={16}
                            sx={{
                              fontSize: '0.875rem',
                              lineHeight: '1.2',
                            }}
                          />
                        ) : (
                          <TruncatedText
                            className="cpSubtitle"
                            maxLength={15}
                            text={company?.address || 'N/A'}
                          />
                        )}
                      </div>
                    </div>
                  </Box>
                )}
              </Box>
            </div>

            <div className="cpHeaderActions">
              {!isEditing && (
                <OlcButton
                  variant="secondary"
                  className="cpSecondaryBtn"
                  onClick={() => setIsEditing(true)}
                >
                  Edit
                </OlcButton>
              )}
              {isEditing && (
                <>
                  <OlcButton
                    variant="secondary"
                    className="cpSecondaryBtn"
                    onClick={handleCancelEdit}
                    disabled={saveLoading}
                  >
                    Cancel
                  </OlcButton>
                  <OlcButton
                    variant="primary"
                    className="cpPrimaryBtn"
                    onClick={handleSaveClick}
                    disabled={saveLoading}
                  >
                    {saveLoading ? (
                      <CircularProgress
                        sx={{
                          color: 'white',
                          width: '20px !important',
                          height: '20px !important',
                        }}
                      />
                    ) : (
                      'Save Changes'
                    )}
                  </OlcButton>
                </>
              )}
            </div>
          </Paper>
        </Box>
        <div className="olcCPScreenCard">
          <Box
            className={`olcScreenCard ${isEditing ? 'activeFieldCard' : ''}`}
            style={{ padding: '0px', marginTop: '12px' }}
          >
            <Paper elevation={0} className="cpCard">
              <Box className="companyProfileTabsHeader">
                <Tabs
                  className="cTabs"
                  value={tab}
                  onChange={(e, v) => setTab(v)}
                  aria-label="Company Profile Tabs"
                >
                  <Tab
                    style={{ padding: '18px 33px' }}
                    label="Settings"
                    id="cp-tab-settings"
                    aria-controls="cp-panel-settings"
                    disableRipple
                  />
                  <Tab
                    style={{ padding: '18px 32px' }}
                    label="Branding Assets"
                    id="cp-tab-branding"
                    aria-controls="cp-panel-branding"
                    disableRipple
                  />
                  <Tab
                    style={{ padding: '18px 32px' }}
                    label="Sender Profiles"
                    id="cp-tab-senders"
                    aria-controls="cp-panel-senders"
                    disableRipple
                  />
                </Tabs>
              </Box>
              <Box className="companyProfileTabsContent">
                <Box
                  className="companyProfileTabPanel"
                  role="tabpanel"
                  hidden={tab !== 0}
                  id="cp-panel-settings"
                  aria-labelledby="cp-tab-settings"
                  sx={{ mt: '20px' }}
                >
                  {tab === 0 && <General />}
                </Box>
                <Box
                  className="companyProfileTabPanel companyProfileTablePanel"
                  role="tabpanel"
                  hidden={tab !== 1}
                  id="cp-panel-branding"
                  aria-labelledby="cp-tab-branding"
                  sx={{ mt: 'var(--olc-list-toolbar-gap)' }}
                >
                  {tab === 1 && <BrandingAssets />}
                </Box>
                <Box
                  className="companyProfileTabPanel companyProfileTablePanel"
                  role="tabpanel"
                  hidden={tab !== 2}
                  id="cp-panel-senders"
                  aria-labelledby="cp-tab-senders"
                  sx={{ mt: 'var(--olc-list-toolbar-gap)' }}
                >
                  {tab === 2 && <SenderProfiles />}
                </Box>
              </Box>
            </Paper>
          </Box>
        </div>
        <ArchiveModal
          openModal={isArchiveModalOpen}
          handleCloseModal={handleCloseArchiveModal}
          onConfirm={handleArchiveConfirm}
          title={`Archive ${company.name}`}
        />
      </Box>
    </>
  );
};

export default CompanyProfile;
